import React, { useEffect, useState } from "react";
import { CaretLeft, X } from "@phosphor-icons/react";
import OTPInput from "components/auth/OTPInput";
import { OTPChannel } from "lib/types/user";
import PrimaryButton from "components/common/buttons/PrimaryButton";
import CircularSpinner from "components/common/loaders/CircularSpinner";
import { useLocale } from "lib/hooks/useLocale";

interface VerifyEmailProps {
  email: string;
  contextId: string;
  onVerify: (otp: string) => Promise<void>;
  onResend: (channel: OTPChannel) => Promise<void>;
  onBack: () => void;
  onClose: () => void;
  isLoading: boolean;
  counter: number;
  counterExpired: boolean;
}

const maskEmail = (email: string): string => {
  const atIndex = email.lastIndexOf("@");
  if (atIndex === -1) return email;
  const local = email.slice(0, atIndex);
  const domain = email.slice(atIndex);
  const visibleStart = Math.min(3, Math.floor(local.length / 2));
  const visibleEnd = Math.min(2, local.length - visibleStart);
  const masked =
    local.slice(0, visibleStart) + "***" + (visibleEnd > 0 ? local.slice(local.length - visibleEnd) : "");
  return `${masked}${domain}`;
};

const VerifyEmail: React.FC<VerifyEmailProps> = ({
  email,
  contextId,
  onVerify,
  onResend,
  onBack,
  onClose,
  isLoading,
  counter,
  counterExpired,
}) => {
  const { t } = useLocale();
  const [otp, setOtp] = useState("");
  const [hasError, setHasError] = useState(false);

  const handleOtpChange = (value: string) => {
    setOtp(value);
    if (hasError) setHasError(false);
  };

  const handleVerify = async () => {
    if (otp.length !== 4 || isLoading) return;
    try {
      await onVerify(otp);
    } catch {
      setHasError(true);
      setOtp("");
    }
  };

  const handleResend = async () => {
    setOtp("");
    setHasError(false);
    await onResend("EMAIL");
  };

  // Auto-submit when OTP is fully entered
  useEffect(() => {
    if (otp.length === 4) {
      handleVerify();
    }
  }, [otp]);

  return (
    <div className="flex w-full flex-col">
      {/* Header */}
      <div className="flex flex-col items-start justify-center px-5 pt-4">
        <div className="flex w-full items-center gap-3">
          {/* Back button */}
          <button
            onClick={onBack}
            className="flex shrink-0 items-center justify-center rounded-lg border border-gray-light bg-white p-1.5 shadow-[0px_1px_2px_0px_rgba(10,13,18,0.05)]">
            <div className="flex h-6 w-6 items-center justify-center">
              <CaretLeft className="h-5 w-5 text-zinc-800" weight="regular" />
            </div>
          </button>

          {/* Title */}
          <div className="flex min-w-0 flex-1 flex-col items-start justify-center">
            <span className="text-text-md-semibold text-zinc-800 whitespace-nowrap">
              {t("shopify_login.verify_email")}
            </span>
          </div>

          {/* Close button */}
          <button onClick={onClose} className="flex shrink-0 items-center justify-center rounded-lg p-1">
            <div className="flex h-5 w-5 items-center justify-center">
              <X className="h-4 w-4 text-zinc-800" weight="regular" />
            </div>
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="flex flex-col items-center justify-center gap-4 p-5">
        <div className="flex w-full flex-col gap-4">
          {/* Subtitle */}
          <p className="text-text-sm-medium text-zinc-500">
            {t("shopify_login.otp_sent_to")}{" "}
            <span className="text-text-sm-semibold text-zinc-800">{maskEmail(email)}</span>
          </p>

          {/* OTP Inputs */}
          <OTPInput
            OTPLength={4}
            disabled={isLoading}
            autoFocus={true}
            hasError={hasError}
            value={otp}
            disableTheme={true}
            onChange={handleOtpChange}
            inputClassName="!rounded-[10px] !h-11 !text-base !border"
          />

          {/* Error */}
          {hasError && (
            <p className="text-center text-text-xs-medium text-ouch">{t("shopify_login.incorrect_otp")}</p>
          )}

          {/* Resend */}
          <div className="flex items-start gap-1">
            <span className="text-text-sm-medium text-zinc-800 opacity-70">
              {t("shopify_login.not_received")}
            </span>
            {counterExpired || counter <= 0 ? (
              <button
                onClick={handleResend}
                disabled={isLoading}
                className="flex items-center gap-1 text-text-sm-semibold text-zinc-800 disabled:opacity-50">
                {isLoading && <CircularSpinner height="h-3" width="w-3" color="text-zinc-800" />}
                {t("resend_otp")}
              </button>
            ) : (
              <span className="text-text-sm-semibold text-zinc-800 opacity-50">
                {t("shopify_login.resend_in", { counter })}
              </span>
            )}
          </div>
        </div>

        {/* Verify CTA */}
        <PrimaryButton
          buttonText={t("shopify_login.verify_and_continue")}
          height="h-11"
          borderRadius="rounded-xl"
          isLoading={isLoading}
          isDisabled={isLoading || otp.length !== 4}
          onClick={handleVerify}
          className="!bg-[#27272a] !text-white"
        />
      </div>
    </div>
  );
};

export default VerifyEmail;
